import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,

  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Delete, AttachFile, CloudUpload } from '@mui/icons-material';
import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { CREATE_OR_UPDATE_TASK_PROGRESS } from '../../graphql/taskProgress';
import LoadingOverlay from '../common/LoadingOverlay';

const TaskProgressModal = ({ open, onClose, taskId, task, taskMetadata }) => {
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [complete, setComplete] = useState(0);
  const [endDate, setEndDate] = useState('');
  const [originalEndDate, setOriginalEndDate] = useState('');
  const [originalComplete, setOriginalComplete] = useState(0);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      const taskComplete = task.complete || 0;
      const taskEndDate = task.end_date ? new Date(task.end_date).toISOString().split('T')[0] : '';
      setComplete(taskComplete);
      setOriginalComplete(taskComplete);
      setEndDate(taskEndDate);
      setOriginalEndDate(taskEndDate);
      setHasChanges(false);
      }
  }, [task]);

  useEffect(() => {
    const progressChanged = complete !== originalComplete;
    const endDateChanged = endDate !== originalEndDate;
    const hasContent = content.trim().length > 0;
    const hasAttachments = attachments.length > 0;
    setHasChanges(progressChanged || endDateChanged || hasContent || hasAttachments);
  }, [complete, endDate, content, attachments, originalComplete, originalEndDate]);

  const handleCompleteChange = (newComplete) => {
    setComplete(newComplete);
    if (newComplete === 100 && !endDate) {
      setEndDate(new Date().toISOString().split('T')[0]);
    } else if (newComplete < 100 && endDate && !originalEndDate) {
      setEndDate('');
    }
  };

  const [createOrUpdateProgress] = useMutation(CREATE_OR_UPDATE_TASK_PROGRESS);
  const [updateTask] = useMutation(gql`
    mutation UpdateTask($id: ID!, $complete: Int, $endDate: String) {
      createOrUpdateTask(id: $id, complete: $complete, end_date: $endDate) {
        task {
          id
          complete
          end_date
        }
        errors
      }
    }
  `);

  const handleFileSelect = (files) => {
    const validFormats = ['png', 'jpg', 'jpeg', 'pdf', 'dwg', 'svg'];
    const newFiles = Array.from(files).filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return validFormats.includes(extension);
    });

    const fileObjects = newFiles.map(file => ({
      id: Date.now() + Math.random(),
      data_file_name: file.name,
      file: file,
      isNew: true
    }));

    setAttachments(prev => [...prev, ...fileObjects]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeAttachment = (attachment) => {
    setAttachments(prev => prev.filter(att => att.id !== attachment.id));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Update task progress and end_date
      await updateTask({
        variables: {
          id: taskId.toString(),
          complete: complete,
          endDate: endDate || null
        }
      });

      // Create progress entry
      await createOrUpdateProgress({
        variables: {
          task_id: parseInt(taskId),
          content: content.trim() || null,
          progress: complete,
          end_date: endDate || null
        }
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setSaving(false);
      resetForm(); 
      onClose();
    }
  };

  const resetForm = () => {
    setContent('');
    setAttachments([]);
    setHasChanges(false);
    };
 
  const handleClose = () => {
    resetForm();
    onClose();
  };


  return (
    <>
      {saving && <LoadingOverlay />}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        scroll="paper"
        sx={{ '& .MuiDialog-paper': { maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
      <DialogTitle>Task Progress</DialogTitle>
      <DialogContent dividers>
        {task && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1.5 }}>
              {task.name}
            </Typography>
            {task.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {task.description}
              </Typography>
            )}
            {taskMetadata && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto 1fr', gap: 1, rowGap: 0.75 }}>
                <Typography variant="body2" color="text.secondary">Project:</Typography>
                <Typography variant="body2">{taskMetadata.projectName}</Typography>
                <Typography variant="body2" color="text.secondary">Dates:</Typography>
                <Typography variant="body2">{taskMetadata.startDate}{taskMetadata.endDate ? ` - ${taskMetadata.endDate}` : ''}</Typography>
                
                <Typography variant="body2" color="text.secondary">Element:</Typography>
                <Typography variant="body2">{taskMetadata.elementName}</Typography>
                <Typography variant="body2" color="text.secondary">Company:</Typography>
                <Typography variant="body2">{taskMetadata.companyName}</Typography>
                
                <Typography variant="body2" color="text.secondary">Stage:</Typography>
                <Typography variant="body2">{taskMetadata.stageName}</Typography>
                <Typography variant="body2" color="text.secondary">Responsible:</Typography>
                <Typography variant="body2">{taskMetadata.responsiblePersons}</Typography>
                
                {taskMetadata.types && (
                  <>
                    <Typography variant="body2" color="text.secondary">Types:</Typography>
                    <Typography variant="body2" sx={{ gridColumn: '2 / -1' }}>{taskMetadata.types}</Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Progress</InputLabel>
              <Select
                value={complete}
                label="Progress"
                onChange={(e) => handleCompleteChange(e.target.value)}
              >
                {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(val => (
                  <MenuItem key={val} value={val}>{val}%</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add a comment..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Attachments</Typography>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragOver ? 'action.hover' : 'transparent',
                transition: 'all 0.2s ease'
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 1 }}>Drop files here or click to upload</Typography>
              <Typography variant="caption" color="text.secondary">
                Supported formats: PNG, JPG, JPEG, PDF, DWG, SVG
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.pdf,.dwg,.svg"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files)}
              />
            </Box>

            {attachments.length > 0 && (
              <List sx={{ mt: 2 }}>
                {attachments.map((attachment) => (
                  <ListItem key={attachment.id} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 1, mb: 1 }}>
                    <AttachFile sx={{ mr: 1, color: 'grey.600' }} />
                    <ListItemText primary={attachment.data_file_name} />
                    <IconButton onClick={() => removeAttachment(attachment)} size="small">
                      <Delete />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleClose}
            >
              CLOSE
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!hasChanges}
            >
              SAVE
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions />
    </Dialog>
    </>
  );
};

export default TaskProgressModal;
